import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Paper, useTheme, useMediaQuery } from '@mui/material';
import { Edit as EditIcon, Settings as SettingsIcon } from '@mui/icons-material';
import { Key, AutoFixHigh, DataThresholding, Link, CheckCircle, Error } from '@mui/icons-material';
import { useGetAssetsQuery } from '@/services/assetApi';

export default function AssetDetail() {
  const { assetId = '', id = '' } = useParams<{ assetId: string; id: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { data: assets } = useGetAssetsQuery(id);
  const asset = assets?.find((a) => a.id === assetId);

  if (!asset) {
    return <Typography>Asset not found</Typography>;
  }

  return (
    <Box p={isMobile ? 1 : 3}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        flexDirection={isMobile ? 'column' : 'row'}
      >
        <Typography variant="h5" sx={{ mb: isMobile ? 2 : 0, alignSelf: 'flex-start' }}>
          {asset.name}
        </Typography>
        <Box display="flex" gap={2} sx={{ width: isMobile ? '100%' : 'auto' }}>
          <Button
            variant="outlined"
            sx={{ width: isMobile ? '100%' : 'auto' }}
            startIcon={<SettingsIcon />}
            onClick={() => navigate(`/amcs/${id}/assets/${assetId}/payment-configs`)}
          >
            Payment Configs
          </Button>
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/amcs/${id}/assets/${assetId}/edit`)}
            sx={{ width: isMobile ? '100%' : 'auto' }}
          >
            Edit Asset
          </Button>
        </Box>
      </Box>

      <Paper
        elevation={3}
        sx={{
          p: { xs: 2, sm: 3 },
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
          gap: { xs: 2, sm: 3 },
          borderRadius: 4,
          backgroundColor: 'background.paper',
        }}
      >
        {[
          { label: 'API Key', value: asset.apiKey, icon: <Key /> },
          {
            label: 'Insert Mandate',
            value: asset.insertAotm ? 'Yes' : 'No',
            icon: <AutoFixHigh />,
          },
          {
            label: 'Housekeeping',
            value: asset.insertDtr ? 'Yes' : 'No',
            icon: <DataThresholding />,
          },
          { label: 'Webhook URL', value: asset.webhookUrl || '-', icon: <Link /> },
          {
            label: 'Webhook Timeout',
            value: asset.webhookTimeout && asset.webhookTimeout > 0 ? `${asset.webhookTimeout} seconds` : '-',
            icon: <SettingsIcon />,
          },
          { label: 'Success Callback', value: asset.callbackSuccess || '-', icon: <CheckCircle /> },
          { label: 'Failure Callback', value: asset.callbackFailure || '-', icon: <Error /> },
        ].map((field, index) => (
          <Box
            key={index}
            sx={{
              p: { xs: 1, sm: 2 },
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
            }}
          >
            <Box display="flex" alignItems="center" gap={1}>
              {field.icon}
              <Typography variant="subtitle1" fontWeight={500}>
                {field.label}
              </Typography>
            </Box>
            <Typography
              variant="body1"
              sx={{
                p: 1,
                backgroundColor: theme.palette.background.default,
                borderRadius: 1,
                wordBreak: 'break-word',
              }}
            >
              {field.value}
            </Typography>
          </Box>
        ))}
      </Paper>
    </Box>
  );
}
